import React, { useEffect, useRef, useState } from "react";

function EnhancedPlayer({
  audioURL,
  Waiting,
  setDisabledButton,
  title,
  SubCategory,
  setIsAudioPlaying,
}) {
  const [timer, setTimer] = useState(Waiting);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(!!audioURL);
  const [recordingTime, setRecordingTime] = useState(0);
  
  const audioRef = useRef(null);

  // Start countdown and play when it reaches 0
  useEffect(() => {
    if (!audioURL) return;

    let cancelled = false;
    const audio = audioRef.current;

    // Reset visible states for each new audio
    setTimer(Waiting);
    setCurrentTime(0);
    setIsPlaying(false);
    setIsPlayingAudio(true);
    setRecordingTime(0);

    const intervalId = setInterval(() => {
      setTimer(prev => {
        if (prev > 1) {
          return prev - 1;
        }
        // Reaching 0 now
        clearInterval(intervalId);
        if (!cancelled && audio) {
          const tryPlay = () => {
            if (audio.readyState >= 2) {
              audio.play().catch((error) => {
                console.error("Error playing audio:", error);
              });
            } else {
              setTimeout(tryPlay, 100);
            }
          };
          tryPlay();
        }
        return 0;
      });
    }, 1000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [audioURL, Waiting]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoadedMetadata = () => setRecordingTime(audio.duration || 0);
    const onTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
    const onPlay = () => { setIsPlaying(true); setIsAudioPlaying(true); setDisabledButton(true); };
    const onPause = () => { setIsPlaying(false); setIsAudioPlaying(false); setDisabledButton(false); };
    const onEnded = () => { setIsPlaying(false); setIsAudioPlaying(false); setDisabledButton(false); };
    const onError = (e) => { console.error("Audio error:", e); setIsPlaying(false); setIsAudioPlaying(false); setDisabledButton(false); };

    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);

    return () => {
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
    };
  }, [audioURL, setDisabledButton, setIsAudioPlaying]);

  // Reset playback position when audioURL changes
  useEffect(() => {
    if (!audioURL) return;
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
  }, [audioURL]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    };
  }, []);

  const getStatusBadge = () => {
    if (timer > 0) {
      return { text: `Starting in ${timer}s`, color: "#f59e0b" };
    } else if (isPlaying) {
      return { text: "Playing", color: "#10b981" };
    } else if (audioURL && !isPlaying) {
      return { text: "Playback Finished", color: "#6b7280" };
    } else {
      return { text: "Ready", color: "#6b7280" };
    }
  };

  const totalDuration = recordingTime || 40;
  const progressPercent = totalDuration > 0 ? Math.min(100, Math.max(0, (currentTime / totalDuration) * 100)) : 0;

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          backgroundColor: "rgba(153, 108, 254, 0.10)",
          maxWidth: "350px",
          borderRadius: "8px",
          padding: "15px",
          margin: "30px auto",
        }}
      >
        <StatusRow>
          <StatusBadge color={getStatusBadge().color}>
            {getStatusBadge().text}
          </StatusBadge>
        </StatusRow>

        <div>
          <audio 
            ref={audioRef} 
            src={audioURL} 
            preload="auto"
            onLoadedData={() => {
              // Audio is ready to play
              console.log('Audio loaded and ready');
            }}
          >
            Your browser does not support the audio element.
          </audio>
          <ProgressWrapper>
            <ProgressTrack />
            <ProgressFill percent={progressPercent} />
            <ProgressThumb percent={progressPercent} />
            <input
              type="range"
              value={currentTime}
              min="0"
              max={totalDuration}
              step="0.1"
              className="slider"
              onChange={() => {}}
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                width: '100%',
                height: 18,
                opacity: 0,
                cursor: 'default',
              }}
            />
          </ProgressWrapper>
        </div>
      </div>
    </div>
  );
}

export default React.memo(EnhancedPlayer);

// Styled Components
function StatusRow({ children }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
      {children}
    </div>
  );
}

function StatusBadge({ color, children }) {
  return (
    <span
      style={{
        fontSize: "12px",
        padding: "3px 8px",
        borderRadius: "999px",
        color: "#fff",
        backgroundColor: color,
      }}
    >
      {children}
    </span>
  );
}

function ProgressWrapper({ children }) {
  return (
    <div style={{ 
      position: 'relative', 
      width: '100%', 
      height: 18, 
      display: 'flex', 
      alignItems: 'center', 
      marginTop: 10 
    }}>
      {children}
    </div>
  );
}

function ProgressTrack() {
  return (
    <div style={{ 
      position: 'absolute', 
      left: 0, 
      right: 0, 
      height: 6, 
      background: '#e2e2ea', 
      borderRadius: 4 
    }} />
  );
}

function ProgressFill({ percent }) {
  return (
    <div style={{ 
      position: 'absolute', 
      left: 0, 
      width: `${percent}%`, 
      height: 6, 
      background: '#996cfe', 
      borderRadius: 4 
    }} />
  );
}

function ProgressThumb({ percent }) {
  return (
    <div style={{ 
      position: 'absolute', 
      left: `calc(${percent}% - 6px)`, 
      width: 12, 
      height: 12, 
      borderRadius: 999, 
      background: '#996cfe' 
    }} />
  );
}
